import {Injectable} from '@angular/core';
import {
  FeaturedProductIconConfig,
  FeaturedProductTileConfig,
  FeaturedProductUrlParamConfig
} from '../models/featured-product';
import {ApplicationStateService} from './application-state.service';
import {UrlParamsService} from '../../core/services/impl/url-params.service';
import {FeaturedProductKey} from '../enums/featured-product-key.enum';
import {addParamsToUrl, env, salesChannel} from '../../core/utils/utils';
import {KeyValueItem} from '../models/key-value-item';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {QueryParamName} from '../../core/enums/query-param-name.enum';
import {SalesChannel} from '../../core/consts/sales-channel.enum';

@Injectable({
  providedIn: 'root'
})
export class FeaturedProductsService {

  private tilesConfig: FeaturedProductTileConfig[];
  private iconsConfig: FeaturedProductIconConfig[];
  private urlParamsConfig: FeaturedProductUrlParamConfig[];

  get featuredProductTiles(): FeaturedProductTileConfig[] {
    return this.tilesConfig;
  }

  get featuredProductIcons(): FeaturedProductIconConfig[] {
    return this.iconsConfig.filter(config => !isNotNullOrUndefined(config.disabled) || !config.disabled);
  }

  constructor(private stateService: ApplicationStateService,
              private urlParamsService: UrlParamsService) { }

  init(tilesConfig: FeaturedProductTileConfig[],
       iconsConfig: FeaturedProductIconConfig[],
       urlParamsConfig: FeaturedProductUrlParamConfig[]): void {
    this.tilesConfig = tilesConfig;
    this.iconsConfig = iconsConfig;
    this.urlParamsConfig = urlParamsConfig;
  }

  getTileUrlWithParams(key: FeaturedProductKey): string {
    const url = this.getTileEndpoint(key);
    if (isNotNullOrUndefined(url)) {
      return decodeURIComponent(addParamsToUrl(url, this.getUrlParams()));
    }
    return null;
  }

  getIconUrlWithParams(key: FeaturedProductKey): string {
    const url = this.getIconEndpoint(key);
    if (isNotNullOrUndefined(url)) {
      return decodeURIComponent(addParamsToUrl(url, this.getUrlParams()));
    }
    return null;
  }

  private getTileEndpoint(key: FeaturedProductKey): string {
    const config = this.getTileConfig(key);
    return isNotNullOrUndefined(config) ? this.getFeaturedProductUrl(config) : null;
  }

  private getIconEndpoint(key: FeaturedProductKey): string {
    const config = this.getIconConfig(key);
    return isNotNullOrUndefined(config) ? this.getFeaturedProductUrl(config) : null;
  }

  private getFeaturedProductUrl(config: FeaturedProductTileConfig | FeaturedProductIconConfig): string {
    const endpointEnvConfig = config.endpoints.find(endpoint => endpoint.env === env());
    let endpointConfig = endpointEnvConfig.urls.find(envConfig => envConfig.salesChannel === salesChannel());
    if (!isNotNullOrUndefined(endpointConfig)) {
      endpointConfig = endpointEnvConfig.urls.find(envConfig => envConfig.default);
    }
    return endpointConfig ? endpointConfig.url : endpointEnvConfig.urls[endpointEnvConfig.urls.length - 1].url;
  }

  private getTileConfig(key: FeaturedProductKey): FeaturedProductTileConfig {
    return this.tilesConfig.find(config => config.key === key);
  }

  private getIconConfig(key: FeaturedProductKey): FeaturedProductIconConfig {
    return this.iconsConfig.find(config => config.key === key);
  }

  private getUrlParams(): KeyValueItem[] {
    return this.urlParamsConfig.map(paramConfig => {
      let paramValue;

      if (paramConfig.staticValue) {
        paramValue = paramConfig.staticValue;
      } else {
        if (isNotNullOrUndefined(paramConfig.businessParamName)) {
          let businessParamValue  = this.stateService.getBusinessParameterValue(paramConfig.businessParamName);
          if (isNotNullOrUndefined(businessParamValue) && paramConfig.valueTransformFn) {
            businessParamValue = paramConfig.valueTransformFn(businessParamValue);
          }
          paramValue = businessParamValue;

          if (!isNotNullOrUndefined(paramValue)) {
            paramValue = this.urlParamsService.getQueryParam(paramConfig.paramName);
          }

          if (!isNotNullOrUndefined(paramValue) && paramConfig.paramName === QueryParamName.PARTNER && salesChannel() !== SalesChannel.KIOSKPOLIS) {
            paramValue = salesChannel();
          }
        }
      }

      return {
        key: paramConfig.paramName,
        value: paramValue
      }
    })
        .filter(param => isNotNullOrUndefined(param.value) && param.value !== '');
  }
}
